import React, { useEffect, useState } from 'react';
import { compose } from 'ramda';
import Dashboard from 'components/Layout/Dashboard/Dashboard';
import withMeta from 'hoc/withMeta';
import Store from 'store/store';
import Router from 'components/Router/Router';
import { Loader, Button, Modal } from 'components/Base';
import { PolicyType } from 'store/modules/Policies/types';
import withCaseInfo from 'hoc/withCaseInfo';
import { makeStyles } from '@material-ui/styles';
import Sidebar from 'components/AdvisorChargesV2/Sidebar';
import Footer from 'components/AdvisorChargesV2/Footer';
import AggregateCostAndCharges from 'components/AdvisorChargesV2/AggregateCostAndCharges/AggregateCostAndCharges';
import Forms from 'components/Forms';
import TabularOverview from 'components/AdvisorChargesV2/TabularOverwiev/TabularOverview';
import { ChargeBase } from 'store/modules/AdvisorCharges/types';
import Charges from 'components/AdvisorChargesV2/Charges/Charges';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolderOutlined';
import api from 'api';
import { fetchDataIfNeeded, getPolicies } from 'components/Cases/utils';
import { useDispatch } from 'react-redux';

const { connect, actions, selectors } = Store;
const { useHistory, useParams } = Router;

const useStyles = makeStyles({
  btnContainer: {
    display: 'flex',
    gap: '15px'
  },
  contentBtn: {
    marginTop: '12px'
  },
  content: {
    marginLeft: '18rem'
  },
  title: {
    fontSize: '1.5rem',
    margin: 0
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between'
  }
});

export interface ChargeApplied {
  amount: string;
  charge_type: ChargeBase;
  id: number;
  percentage_value: number;
  applied_charges: {
    amount: string;
    id: number;
    policy_id: number | null;
  }[];
}

interface AdvisorChargesProps {
  recommendedPolicies: Array<PolicyType>;
  refreshPolicyData: ({
    portfolio,
    policyId
  }: {
    portfolio: string;
    policyId: number;
  }) => Promise<any>;
  totalChargesInOneYear: {
    totalInitial: number;
    totalOngoing: number;
  };
  setAggregateCostAndCharges: (data: any) => void;
  setAggregateCostAndChargesTable: (data: any) => void;
}

const AdvisorChargesPage = ({
  recommendedPolicies,
  refreshPolicyData,
  totalChargesInOneYear,
  setAggregateCostAndCharges,
  setAggregateCostAndChargesTable
}: AdvisorChargesProps) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCharges, setIsLoadingCharges] = useState(false);
  const [showConsolidated, setShowConsolidated] = useState(false);
  const [isInitialChargeModal, setInitialChargeModal] = useState(false);
  const [isOngoingChargeModal, setOngoingChargeModal] = useState(false);
  const [activePolicy, setActivePolicy] = useState<null | number>(null);
  const { portfolio } = useParams() as any;
  const classes = useStyles();
  const history = useHistory();

  useEffect(() => {
    const arePolicies = recommendedPolicies.length > 0;

    if (arePolicies) {
      setActivePolicy(recommendedPolicies[0].id);
    }
    // eslint-disable-next-line
  }, [isLoading]);

  const getData = () => {
    dispatch(getPolicies(portfolio) as any).then(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    setIsLoading(true);

    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activePolicyDetails =
    activePolicy &&
    (recommendedPolicies.find(policy => policy.id === activePolicy) as any);

  useEffect(() => {
    //if no activePolicyDetails.id and showConsolidated
    const policyId = activePolicyDetails ? activePolicyDetails.id : null;

    if (!policyId && !showConsolidated) {
      return;
    }

    setIsLoadingCharges(true);
    dispatch(
      fetchDataIfNeeded(`allAggregateCostAndCharges.${policyId}`, () =>
        api.getAggregateCostAndCharges({ portfolio, policyId })
      ) as any
    ).then((response: any) => {
      const data = response?.data || response;
      setAggregateCostAndCharges({ data: data.chart, id: policyId });
      setAggregateCostAndChargesTable({ data: data.table, id: policyId });
      setIsLoadingCharges(false);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePolicyDetails && activePolicyDetails.id, showConsolidated]);

  return (
    <Dashboard wrapBreadcrumbs={!!activePolicyDetails}>
      <Loader isLoading={isLoading}>
        {activePolicyDetails || showConsolidated ? (
          <>
            <div style={{ paddingBottom: '86px', marginTop: '28px' }}>
              <Sidebar
                policies={recommendedPolicies}
                activePolicy={activePolicy}
                setActivePolicy={setActivePolicy}
                setShowConsolidated={setShowConsolidated}
                showConsolidated={showConsolidated}
              />

              {!showConsolidated ? (
                <div className={classes.content}>
                  <header className={classes.header}>
                    <h5 className={classes.title}>
                      {activePolicyDetails.display_name}
                    </h5>
                    <div className={classes.btnContainer}>
                      <Button
                        style={{ marginBottom: '1rem' }}
                        type="button"
                        onClick={() => setInitialChargeModal(true)}
                      >
                        Add Initial charge
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setOngoingChargeModal(true)}
                      >
                        Add Ongoing charge
                      </Button>
                    </div>
                  </header>

                  <Charges
                    portfolio={portfolio}
                    policies={recommendedPolicies}
                    activePolicy={activePolicyDetails}
                    refreshPolicyData={() =>
                      refreshPolicyData({
                        portfolio,
                        policyId: activePolicyDetails.id
                      })
                    }
                  />

                  <AggregateCostAndCharges
                    isLoading={isLoadingCharges}
                    policyId={
                      activePolicyDetails ? activePolicyDetails.id : null
                    }
                  />

                  <TabularOverview
                    isLoading={isLoadingCharges}
                    policyId={
                      activePolicyDetails ? activePolicyDetails.id : null
                    }
                  />
                </div>
              ) : (
                <div className={classes.content}>
                  <header className={classes.header}>
                    <h5 className={classes.title}>Consolidated charges</h5>
                  </header>

                  <AggregateCostAndCharges isLoading={isLoadingCharges} />

                  <TabularOverview isLoading={isLoadingCharges} />
                </div>
              )}
            </div>
            <Footer
              portfolio={portfolio}
              totalInitial={Number(totalChargesInOneYear.totalInitial)}
              totalOngoing={totalChargesInOneYear.totalOngoing}
            />

            <Modal
              isOpen={isInitialChargeModal}
              disableEscapeKeyDown
              disableBackdropClick
              closeModalFunc={() => setInitialChargeModal(false)}
            >
              <Forms.v2.AdviserCharges.Initial
                closeModal={() => setInitialChargeModal(false)}
                policies={recommendedPolicies}
                activePolicy={activePolicyDetails}
                portfolio={portfolio}
                cta={() =>
                  refreshPolicyData({
                    portfolio,
                    policyId: activePolicyDetails.id
                  })
                }
              />
            </Modal>

            <Modal
              isOpen={isOngoingChargeModal}
              disableEscapeKeyDown
              disableBackdropClick
              closeModalFunc={() => setOngoingChargeModal(false)}
            >
              <Forms.v2.AdviserCharges.Ongoing
                closeModal={() => setOngoingChargeModal(false)}
                policies={recommendedPolicies}
                activePolicy={activePolicyDetails}
                portfolio={portfolio}
                cta={() =>
                  refreshPolicyData({
                    portfolio,
                    policyId: activePolicyDetails.id
                  })
                }
              />
            </Modal>
          </>
        ) : (
          <div
            style={{
              textAlign: 'center',
              marginBottom: '1rem',
              padding: '2rem'
            }}
          >
            <CreateNewFolderIcon
              htmlColor="#6b7280"
              style={{ width: '36px', height: '36px' }}
            />
            <div style={{ fontWeight: 600 }}>No Recommended Policies</div>
            <div style={{ color: '#6b7280' }}>
              To see adviser charges please create a recommended policy.
            </div>
            <div
              style={{
                padding: '1rem 0rem 0rem'
              }}
            >
              <Button
                onClick={() => history.push(`/${portfolio}/recommendations`)}
              >
                View Recommendations
              </Button>
            </div>
          </div>
        )}
      </Loader>
    </Dashboard>
  );
};

export default compose(
  connect(
    state => ({
      initialAdvisorChargesRequests:
        selectors.caseSelectors.advisorCharges(state),
      ongoingAdvisorChargesRequests:
        selectors.caseSelectors.advisorCharges(state),
      appliedCharges: selectors.caseSelectors.appliedCharges(state),
      recommendedPolicies: selectors.caseSelectors.policies(state),
      totalChargesInOneYear:
        selectors.caseSelectors.totalOngoingChargeInOneYear(state)
    }),
    {
      refreshPolicyData: actions.caseActions.refreshPolicyData,
      setAggregateCostAndCharges:
        actions.caseActions.setAggregateCostAndCharges,
      setAggregateCostAndChargesTable:
        actions.caseActions.setAggregateCostAndChargesTable
    }
  ),
  withMeta,
  withCaseInfo
)(AdvisorChargesPage);
